import { create } from 'zustand';

interface PromptState {
  open: boolean;
  title: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  resolve: ((value: string | null) => void) | null;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmLabel?: string;
  danger: boolean;
  loading: boolean;
  resolve: ((value: boolean) => void) | null;
  /** Async work to run after the user confirms. If it throws, the dialog stays
   *  open with loading=false so the user can retry or cancel. */
  onConfirm: (() => Promise<void> | void) | null;
}

interface DialogStore {
  prompt: PromptState;
  confirm: ConfirmState;

  showPrompt: (options: {
    title: string;
    message?: string;
    defaultValue?: string;
    placeholder?: string;
  }) => Promise<string | null>;

  closePrompt: (value: string | null) => void;

  /**
   * Open the type-to-confirm destructive dialog. The returned Promise resolves
   * to true when the user confirmed and onConfirm succeeded, false when they
   * cancelled, and rejects if onConfirm throws (the dialog closes on success,
   * stays open on error so the user can see the toast and retry).
   */
  showConfirm: (options: {
    title: string;
    message: string;
    confirmText: string;
    confirmLabel?: string;
    danger?: boolean;
    onConfirm: () => Promise<void> | void;
  }) => Promise<boolean>;

  setConfirmLoading: (loading: boolean) => void;
  closeConfirm: (confirmed: boolean) => void;
}

export const useDialogStore = create<DialogStore>((set, get) => ({
  prompt: { open: false, title: '', resolve: null },
  confirm: {
    open: false,
    title: '',
    message: '',
    confirmText: '',
    danger: true,
    loading: false,
    resolve: null,
    onConfirm: null,
  },

  showPrompt: (options) => {
    return new Promise<string | null>((resolve) => {
      set({
        prompt: {
          open: true,
          title: options.title,
          message: options.message,
          defaultValue: options.defaultValue,
          placeholder: options.placeholder,
          resolve,
        },
      });
    });
  },

  closePrompt: (value) => {
    const { prompt } = get();
    if (prompt.resolve) prompt.resolve(value);
    set({
      prompt: { open: false, title: '', resolve: null },
    });
  },

  showConfirm: (options) => {
    return new Promise<boolean>((resolve) => {
      set({
        confirm: {
          open: true,
          title: options.title,
          message: options.message,
          confirmText: options.confirmText,
          confirmLabel: options.confirmLabel,
          danger: options.danger ?? true,
          loading: false,
          resolve,
          onConfirm: options.onConfirm,
        },
      });
    });
  },

  setConfirmLoading: (loading) => {
    set((state) => ({ confirm: { ...state.confirm, loading } }));
  },

  closeConfirm: (confirmed) => {
    const { confirm } = get();
    if (confirm.resolve) confirm.resolve(confirmed);
    set({
      confirm: {
        open: false,
        title: '',
        message: '',
        confirmText: '',
        danger: true,
        loading: false,
        resolve: null,
        onConfirm: null,
      },
    });
  },
}));
